import { useState, useEffect, useCallback } from "react";
import {
  Calculator, Download, FileText, Receipt, TrendingUp, 
  Clock, CheckCircle, AlertCircle, Plus, Search, Filter,
  Euro, FileSpreadsheet, ArrowUpRight, ArrowDownRight,
  Calendar, RefreshCw, Loader2, Settings, Book, CreditCard,
  Building2, Users, Wallet, PiggyBank, ClipboardList, BarChart3,
  FileCheck, FileMinus, FileInput, CalendarDays, List, Landmark,
  Eye, Edit, Trash2, Send, Check, X, AlertTriangle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../components/ui/table";
import { toast } from "sonner";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value || 0);
};

// Helper to format date
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString('it-IT');
};

// Helper to download Excel files with date range
const downloadExcel = async (endpoint, filename, startDate = null, endDate = null) => {
  try {
    let url = `${API}/fiscal/xlsx/${endpoint}`;
    const params = new URLSearchParams();
    
    // Parse existing query params from endpoint
    if (endpoint.includes('?')) {
      const [base, query] = endpoint.split('?');
      url = `${API}/fiscal/xlsx/${base}`;
      new URLSearchParams(query).forEach((v, k) => params.set(k, v));
    }
    
    // Add date filters if provided
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    
    const queryString = params.toString();
    const fullUrl = queryString ? `${url}?${queryString}` : url;
    
    const response = await authFetch(fullUrl);
    if (!response.ok) throw new Error("Errore download");
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(blobUrl);
    document.body.removeChild(a);
    toast.success("File Excel scaricato");
  } catch (error) {
    console.error("Download error:", error);
    toast.error("Errore durante il download");
  }
};

// Codici Natura IVA per aliquota 0% - Include tutti i codici Reverse Charge N6.x
const NATURE_IVA = [
  { code: "", label: "-- Seleziona Natura --" },
  // Escluse e Non soggette
  { code: "N1", label: "N1 - Escluse ex art.15" },
  { code: "N2.1", label: "N2.1 - Non soggette artt. 7-7septies" },
  { code: "N2.2", label: "N2.2 - Non soggette altri casi" },
  // Non imponibili
  { code: "N3.1", label: "N3.1 - Non imponibili esportazioni" },
  { code: "N3.2", label: "N3.2 - Non imponibili cessioni intraUE" },
  { code: "N3.3", label: "N3.3 - Non imponibili San Marino" },
  { code: "N3.4", label: "N3.4 - Non imponibili op. assimilate" },
  { code: "N3.5", label: "N3.5 - Non imponibili dich. intento" },
  { code: "N3.6", label: "N3.6 - Non imponibili altre op." },
  // Esenti e Regime margine
  { code: "N4", label: "N4 - Esenti art.10" },
  { code: "N5", label: "N5 - Regime del margine" },
  // REVERSE CHARGE - Inversione contabile (tutti i codici N6.x)
  { code: "N6.1", label: "N6.1 - RC Rottami/recupero" },
  { code: "N6.2", label: "N6.2 - RC Oro/argento puro" },
  { code: "N6.3", label: "N6.3 - RC Subappalto edilizia" },
  { code: "N6.4", label: "N6.4 - RC Cessione fabbricati" },
  { code: "N6.5", label: "N6.5 - RC Telefoni cellulari" },
  { code: "N6.6", label: "N6.6 - RC Prodotti elettronici" },
  { code: "N6.7", label: "N6.7 - RC Prestazioni edili" },
  { code: "N6.8", label: "N6.8 - RC Settore energetico" },
  { code: "N6.9", label: "N6.9 - RC Altri casi" },
  // IVA assolta in UE
  { code: "N7", label: "N7 - IVA assolta in altro Stato UE" }
];

// Date Filter Component for each section
const DateRangeFilter = ({ startDate, endDate, onStartChange, onEndChange, onDownload, downloadLabel = "Scarica Excel" }) => (
  <div className="flex flex-wrap items-center gap-3 p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
    <div className="flex items-center gap-2">
      <Calendar className="w-4 h-4 text-zinc-400" />
      <span className="text-sm text-zinc-400">Filtra per data:</span>
    </div>
    <div className="flex items-center gap-2">
      <label className="text-xs text-zinc-500">Da:</label>
      <Input
        type="date"
        value={startDate}
        onChange={(e) => onStartChange(e.target.value)}
        className="w-40 h-8 bg-zinc-800 border-zinc-600 text-sm"
      />
    </div>
    <div className="flex items-center gap-2">
      <label className="text-xs text-zinc-500">A:</label>
      <Input
        type="date"
        value={endDate}
        onChange={(e) => onEndChange(e.target.value)}
        className="w-40 h-8 bg-zinc-800 border-zinc-600 text-sm"
      />
    </div>
    <Button
      onClick={onDownload}
      size="sm"
      className="bg-primary hover:bg-primary/80"
      data-testid="download-filtered-btn"
    >
      <Download className="w-4 h-4 mr-2" />
      {downloadLabel}
    </Button>
  </div>
);

export default function FiscalModule({ user }) {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [activeSection, setActiveSection] = useState("documenti");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  
  // Date range filters for each section
  const getDefaultStartDate = () => {
    const date = new Date();
    date.setMonth(0, 1); // January 1st of current year
    return date.toISOString().split('T')[0];
  };
  const getDefaultEndDate = () => new Date().toISOString().split('T')[0];
  
  const [dateFilters, setDateFilters] = useState({
    documenti: { start: getDefaultStartDate(), end: getDefaultEndDate() },
    passivi: { start: getDefaultStartDate(), end: getDefaultEndDate() },
    autofatture: { start: getDefaultStartDate(), end: getDefaultEndDate() },
    scadenzario: { start: getDefaultStartDate(), end: getDefaultEndDate() },
    riepiloghi: { start: getDefaultStartDate(), end: getDefaultEndDate() },
    primaNota: { start: getDefaultStartDate(), end: getDefaultEndDate() },
    estrattoConto: { start: getDefaultStartDate(), end: getDefaultEndDate() },
    registroIva: { start: getDefaultStartDate(), end: getDefaultEndDate() },
    pagamenti: { start: getDefaultStartDate(), end: getDefaultEndDate() }
  });
  
  const updateDateFilter = (section, field, value) => {
    setDateFilters(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };
  
  // Data states
  const [documentiAttivi, setDocumentiAttivi] = useState([]);
  const [documentiPassivi, setDocumentiPassivi] = useState([]);
  const [autofatture, setAutofatture] = useState([]);
  const [scadenze, setScadenze] = useState([]);
  const [riepiloghi, setRiepiloghi] = useState(null);
  const [primaNota, setPrimaNota] = useState([]);
  const [pianoConti, setPianoConti] = useState([]);
  const [pianoContiTotali, setPianoContiTotali] = useState({ attivo: 0, passivo: 0, ricavi: 0, costi: 0 });
  const [fido, setFido] = useState([]);
  const [estrattoConto, setEstrattoConto] = useState([]);
  const [registroIva, setRegistroIva] = useState({ vendite: [], acquisti: [] });
  const [pagamentiIncassi, setPagamentiIncassi] = useState({ movimenti: [], totale_incassi: 0, totale_pagamenti: 0, saldo: 0, periodo: "" });
  
  // Dialog states
  const [showDocPassivoDialog, setShowDocPassivoDialog] = useState(false);
  const [showAutofatturaDialog, setShowAutofatturaDialog] = useState(false);
  const [showScadenzaDialog, setShowScadenzaDialog] = useState(false);
  const [showPrimaNotaDialog, setShowPrimaNotaDialog] = useState(false);
  const [showContoDialog, setShowContoDialog] = useState(false);
  const [showFidoDialog, setShowFidoDialog] = useState(false);
  const [showPagamentoDialog, setShowPagamentoDialog] = useState(false);
  const [showMovimentiContoDialog, setShowMovimentiContoDialog] = useState(false);
  const [selectedContoMovimenti, setSelectedContoMovimenti] = useState(null);
  const [movimentiContoLoading, setMovimentiContoLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Form states
  const [docPassivoForm, setDocPassivoForm] = useState({
    numero_documento: "",
    data_documento: new Date().toISOString().split("T")[0],
    fornitore: "",
    partita_iva_fornitore: "",
    descrizione: "",
    imponibile: 0,
    aliquota_iva: 22,
    natura_iva: "", // Codice Natura per IVA 0% (N1-N7, N6.x per Reverse Charge)
    iva: 0,
    totale: 0,
    data_scadenza: "",
    stato_pagamento: "da_pagare"
  });

  const [autofatturaForm, setAutofatturaForm] = useState({
    tipo_documento: "TD17",
    numero_documento: "",
    data_documento: new Date().toISOString().split("T")[0],
    fornitore_estero: "",
    paese: "",
    descrizione: "",
    imponibile: 0,
    aliquota_iva: 22,
    natura_iva: "", // Codice Natura per IVA 0% (N6.x per autofatture reverse charge)
    iva: 0
  });

  const [scadenzaForm, setScadenzaForm] = useState({
    tipo: "pagamento",
    descrizione: "",
    importo: 0,
    data_scadenza: "",
    cliente_fornitore: "",
    documento_riferimento: "",
    stato: "aperta"
  });

  const [primaNotaForm, setPrimaNotaForm] = useState({
    data_registrazione: new Date().toISOString().split("T")[0],
    descrizione: "",
    conto_dare: "",
    conto_avere: "",
    importo: 0,
    documento_riferimento: ""
  });

  const [contoForm, setContoForm] = useState({
    codice: "",
    descrizione: "",
    tipo: "patrimoniale",
    categoria: "",
    saldo_iniziale: 0
  });

  const [fidoForm, setFidoForm] = useState({
    cliente_id: "",
    cliente_nome: "",
    fido_accordato: 0,
    fido_utilizzato: 0,
    stato: "attivo"
  });

  // Fetch config and initial data
  const fetchConfig = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/fiscal/config`, { });
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        return data;
      }
    } catch (error) {
      console.error("Error fetching config:", error);
    }
    return null;
  }, []);

  // Fetch documenti fiscali attivi (fatture emesse)
  const fetchDocumentiAttivi = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/fiscal/documenti-attivi?year=${selectedYear}`, { });
      if (res.ok) {
        setDocumentiAttivi(await res.json());
      }
    } catch (error) {
      console.error("Error fetching documenti attivi:", error);
    }
  }, [selectedYear]);

  // Fetch documenti fiscali passivi (fatture ricevute)
  const fetchDocumentiPassivi = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/fiscal/documenti-passivi?year=${selectedYear}`, { });
      if (res.ok) {
        setDocumentiPassivi(await res.json());
      }
    } catch (error) {
      console.error("Error fetching documenti passivi:", error);
    }
  }, [selectedYear]);

  // Fetch autofatture
  const fetchAutofatture = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/fiscal/autofatture?year=${selectedYear}`, { });
      if (res.ok) {
        setAutofatture(await res.json());
      }
    } catch (error) {
      console.error("Error fetching autofatture:", error);
    }
  }, [selectedYear]);

  // Fetch scadenzario
  const fetchScadenze = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/fiscal/scadenzario?year=${selectedYear}&month=${selectedMonth}`, { });
      if (res.ok) {
        setScadenze(await res.json());
      }
    } catch (error) {
      console.error("Error fetching scadenze:", error);
    }
  }, [selectedYear, selectedMonth]);

  // Fetch riepiloghi fiscali
  const fetchRiepiloghi = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/fiscal/riepiloghi?year=${selectedYear}`, { });
      if (res.ok) {
        setRiepiloghi(await res.json());
      }
    } catch (error) {
      console.error("Error fetching riepiloghi:", error);
    }
  }, [selectedYear]);

  // Fetch prima nota
  const fetchPrimaNota = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/fiscal/prima-nota?year=${selectedYear}&month=${selectedMonth}`, { });
      if (res.ok) {
        setPrimaNota(await res.json());
      }
    } catch (error) {
      console.error("Error fetching prima nota:", error);
    }
  }, [selectedYear, selectedMonth]);

  // Fetch piano dei conti
  const fetchPianoConti = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/fiscal/piano-conti`, { });
      if (res.ok) {
        const data = await res.json();
        // Support both old format (array) and new format (object with conti and totali)
        if (Array.isArray(data)) {
          setPianoConti(data);
        } else {
          setPianoConti(data.conti || []);
          // Store totals if available
          if (data.totali) {
            setPianoContiTotali(data.totali);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching piano conti:", error);
    }
  }, []);

  // Fetch movimenti for a specific conto
  const fetchMovimentiConto = async (contoId) => {
    setMovimentiContoLoading(true);
    try {
      const res = await authFetch(`${API}/fiscal/piano-conti/${contoId}/movimenti`, { });
      if (res.ok) {
        const data = await res.json();
        setSelectedContoMovimenti(data);
        setShowMovimentiContoDialog(true);
      } else {
        toast.error("Errore nel caricamento dei movimenti");
      }
    } catch (error) {
      console.error("Error fetching movimenti:", error);
      toast.error("Errore di connessione");
    } finally {
      setMovimentiContoLoading(false);
    }
  };

  // Fetch gestione fido
  const fetchFido = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/fiscal/fido`, { });
      if (res.ok) {
        setFido(await res.json());
      }
    } catch (error) {
      console.error("Error fetching fido:", error);
    }
  }, []);

  // Fetch estratto conto
  const fetchEstrattoConto = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/fiscal/estratto-conto?year=${selectedYear}&month=${selectedMonth}`, { });
      if (res.ok) {
        setEstrattoConto(await res.json());
      }
    } catch (error) {
      console.error("Error fetching estratto conto:", error);
    }
  }, [selectedYear, selectedMonth]);

  // Fetch registro IVA
  const fetchRegistroIva = useCallback(async () => {
    try {
      const [venditeRes, acquistiRes] = await Promise.all([
        authFetch(`${API}/fiscal/registro-iva/vendite?year=${selectedYear}`, { }),
        authFetch(`${API}/fiscal/registro-iva/acquisti?year=${selectedYear}`, { })
      ]);
      
      const vendite = venditeRes.ok ? await venditeRes.json() : [];
      const acquisti = acquistiRes.ok ? await acquistiRes.json() : [];
      setRegistroIva({ vendite, acquisti });
    } catch (error) {
      console.error("Error fetching registro IVA:", error);
    }
  }, [selectedYear]);

  // Fetch pagamenti e incassi
  const fetchPagamentiIncassi = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/fiscal/pagamenti-incassi?year=${selectedYear}&month=${selectedMonth}`, { });
      if (res.ok) {
        setPagamentiIncassi(await res.json());
      }
    } catch (error) {
      console.error("Error fetching pagamenti/incassi:", error);
    }
  }, [selectedYear, selectedMonth]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const cfg = await fetchConfig();
      if (cfg?.enabled) {
        await Promise.all([
          fetchDocumentiAttivi(),
          fetchDocumentiPassivi(),
          fetchAutofatture(),
          fetchScadenze(),
          fetchRiepiloghi(),
          fetchPrimaNota(),
          fetchPianoConti(),
          fetchFido(),
          fetchEstrattoConto(),
          fetchRegistroIva(),
          fetchPagamentiIncassi()
        ]);
      }
      setLoading(false);
    };
    loadData();
  }, []);

  // Reload when year/month changes
  useEffect(() => {
    if (config?.enabled) {
      fetchDocumentiAttivi();
      fetchDocumentiPassivi();
      fetchAutofatture();
      fetchScadenze();
      fetchRiepiloghi();
      fetchPrimaNota();
      fetchEstrattoConto();
      fetchRegistroIva();
      fetchPagamentiIncassi();
    }
  }, [selectedYear, selectedMonth]);

  // Calculate IVA for doc passivo
  useEffect(() => {
    const imponibile = parseFloat(docPassivoForm.imponibile) || 0;
    const aliquota = parseFloat(docPassivoForm.aliquota_iva) || 22;
    const iva = imponibile * (aliquota / 100);
    setDocPassivoForm(prev => ({ 
      ...prev, 
      iva: iva.toFixed(2),
      totale: (imponibile + iva).toFixed(2)
    }));
  }, [docPassivoForm.imponibile, docPassivoForm.aliquota_iva]);

  // Calculate IVA for autofattura
  useEffect(() => {
    const imponibile = parseFloat(autofatturaForm.imponibile) || 0;
    const aliquota = parseFloat(autofatturaForm.aliquota_iva) || 22;
    const iva = imponibile * (aliquota / 100);
    setAutofatturaForm(prev => ({ ...prev, iva: iva.toFixed(2) }));
  }, [autofatturaForm.imponibile, autofatturaForm.aliquota_iva]);

  // CRUD Operations
  const handleSaveDocPassivo = async () => {
    try {
      const method = editingItem ? "PUT" : "POST";
      const url = editingItem 
        ? `${API}/fiscal/documenti-passivi/${editingItem.id}`
        : `${API}/fiscal/documenti-passivi`;
      
      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(docPassivoForm)
      });

      if (res.ok) {
        toast.success(editingItem ? "Documento aggiornato" : "Documento registrato");
        setShowDocPassivoDialog(false);
        setEditingItem(null);
        resetDocPassivoForm();
        fetchDocumentiPassivi();
        fetchScadenze();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Errore nel salvataggio");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const handleSaveAutofattura = async () => {
    try {
      const method = editingItem ? "PUT" : "POST";
      const url = editingItem 
        ? `${API}/fiscal/autofatture/${editingItem.id}`
        : `${API}/fiscal/autofatture`;
      
      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(autofatturaForm)
      });

      if (res.ok) {
        toast.success(editingItem ? "Autofattura aggiornata" : "Autofattura creata");
        setShowAutofatturaDialog(false);
        setEditingItem(null);
        resetAutofatturaForm();
        fetchAutofatture();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Errore nel salvataggio");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const handleSaveScadenza = async () => {
    try {
      const method = editingItem ? "PUT" : "POST";
      const url = editingItem 
        ? `${API}/fiscal/scadenzario/${editingItem.id}`
        : `${API}/fiscal/scadenzario`;
      
      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(scadenzaForm)
      });

      if (res.ok) {
        toast.success(editingItem ? "Scadenza aggiornata" : "Scadenza creata");
        setShowScadenzaDialog(false);
        setEditingItem(null);
        resetScadenzaForm();
        fetchScadenze();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Errore nel salvataggio");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const handleSavePrimaNota = async () => {
    try {
      const method = editingItem ? "PUT" : "POST";
      const url = editingItem 
        ? `${API}/fiscal/prima-nota/${editingItem.id}`
        : `${API}/fiscal/prima-nota`;
      
      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(primaNotaForm)
      });

      if (res.ok) {
        toast.success(editingItem ? "Registrazione aggiornata" : "Registrazione creata");
        setShowPrimaNotaDialog(false);
        setEditingItem(null);
        resetPrimaNotaForm();
        fetchPrimaNota();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Errore nel salvataggio");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const handleSaveConto = async () => {
    try {
      const method = editingItem ? "PUT" : "POST";
      const url = editingItem 
        ? `${API}/fiscal/piano-conti/${editingItem.id}`
        : `${API}/fiscal/piano-conti`;
      
      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(contoForm)
      });

      if (res.ok) {
        toast.success(editingItem ? "Conto aggiornato" : "Conto creato");
        setShowContoDialog(false);
        setEditingItem(null);
        resetContoForm();
        fetchPianoConti();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Errore nel salvataggio");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const handleSaveFido = async () => {
    try {
      const method = editingItem ? "PUT" : "POST";
      const url = editingItem 
        ? `${API}/fiscal/fido/${editingItem.id}`
        : `${API}/fiscal/fido`;
      
      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fidoForm)
      });

      if (res.ok) {
        toast.success(editingItem ? "Fido aggiornato" : "Fido creato");
        setShowFidoDialog(false);
        setEditingItem(null);
        resetFidoForm();
        fetchFido();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Errore nel salvataggio");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const handleMarkScadenzaPagata = async (scadenzaId) => {
    try {
      const res = await authFetch(`${API}/fiscal/scadenzario/${scadenzaId}/paga`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data_pagamento: new Date().toISOString() })
      });

      if (res.ok) {
        toast.success("Scadenza marcata come pagata");
        fetchScadenze();
      }
    } catch (error) {
      toast.error("Errore");
    }
  };

  const handleDelete = async (type, id) => {
    if (!confirm("Sei sicuro di voler eliminare questo elemento?")) return;
    
    try {
      const res = await authFetch(`${API}/fiscal/${type}/${id}`, {
        method: "DELETE"
      });

      if (res.ok) {
        toast.success("Elemento eliminato");
        // Refresh appropriate data
        switch(type) {
          case "documenti-passivi": fetchDocumentiPassivi(); break;
          case "autofatture": fetchAutofatture(); break;
          case "scadenzario": fetchScadenze(); break;
          case "prima-nota": fetchPrimaNota(); break;
          case "piano-conti": fetchPianoConti(); break;
          case "fido": fetchFido(); break;
        }
      }
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Reset form functions
  const resetDocPassivoForm = () => {
    setDocPassivoForm({
      numero_documento: "",
      data_documento: new Date().toISOString().split("T")[0],
      fornitore: "",
      partita_iva_fornitore: "",
      descrizione: "",
      imponibile: 0,
      aliquota_iva: 22,
      iva: 0,
      totale: 0,
      data_scadenza: "",
      stato_pagamento: "da_pagare"
    });
  };

  const resetAutofatturaForm = () => {
    setAutofatturaForm({
      tipo_documento: "TD17",
      numero_documento: "",
      data_documento: new Date().toISOString().split("T")[0],
      fornitore_estero: "",
      paese: "",
      descrizione: "",
      imponibile: 0,
      aliquota_iva: 22,
      iva: 0
    });
  };

  const resetScadenzaForm = () => {
    setScadenzaForm({
      tipo: "pagamento",
      descrizione: "",
      importo: 0,
      data_scadenza: "",
      cliente_fornitore: "",
      documento_riferimento: "",
      stato: "aperta"
    });
  };

  const resetPrimaNotaForm = () => {
    setPrimaNotaForm({
      data_registrazione: new Date().toISOString().split("T")[0],
      descrizione: "",
      conto_dare: "",
      conto_avere: "",
      importo: 0,
      documento_riferimento: ""
    });
  };

  const resetContoForm = () => {
    setContoForm({
      codice: "",
      descrizione: "",
      tipo: "patrimoniale",
      categoria: "",
      saldo_iniziale: 0
    });
  };

  const resetFidoForm = () => {
    setFidoForm({
      cliente_id: "",
      cliente_nome: "",
      fido_accordato: 0,
      fido_utilizzato: 0,
      stato: "attivo"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="fiscal-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!config?.enabled) {
    return (
      <div className="text-center py-12" data-testid="fiscal-disabled">
        <Calculator className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-white mb-2">Modulo Fiscale Non Abilitato</h2>
        <p className="text-zinc-400 mb-4">
          Contatta l'amministratore per abilitare il modulo di gestione fiscale.
        </p>
        <Badge className="bg-zinc-700 text-zinc-300">Richiede attivazione Admin</Badge>
      </div>
    );
  }

  const menuItems = [
    { id: "documenti", label: "Documenti Fiscali", icon: FileText },
    { id: "passivi", label: "Documenti Passivi", icon: FileMinus },
    { id: "autofatture", label: "Autofatture", icon: FileInput },
    { id: "scadenzario", label: "Scadenzario", icon: CalendarDays },
    { id: "riepiloghi", label: "Riepiloghi Fiscali", icon: BarChart3 },
    { id: "prima-nota", label: "Prima Nota", icon: ClipboardList },
    { id: "piano-conti", label: "Piano dei Conti", icon: List },
    { id: "fido", label: "Gestione Fido", icon: CreditCard },
    { id: "estratto-conto", label: "Estratto Conto", icon: Wallet },
    { id: "registro-iva", label: "Registro IVA", icon: Book },
    { id: "pagamenti", label: "Pagamenti e Incassi", icon: Euro },
  ];

  return (
    <div className="space-y-6" data-testid="fiscal-module">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Gestione Fiscale</h1>
          <span className="text-zinc-400 flex items-center gap-2">
            Regime: <Badge className={config.regime === "ORDINARIO" ? "bg-blue-500/20 text-blue-400" : "bg-green-500/20 text-green-400"}>
              {config.regime}
            </Badge>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-28 bg-zinc-800 border-zinc-700">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026].map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
            <SelectTrigger className="w-36 bg-zinc-800 border-zinc-700">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", 
                "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"].map((m, i) => (
                <SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex flex-wrap gap-2 p-2 bg-zinc-900 rounded-lg">
        {menuItems.map(item => (
          <Button
            key={item.id}
            variant={activeSection === item.id ? "default" : "ghost"}
            size="sm"
            onClick={() => setActiveSection(item.id)}
            className={activeSection === item.id ? "bg-primary" : "text-zinc-400 hover:text-white"}
          >
            <item.icon className="w-4 h-4 mr-2" />
            {item.label}
          </Button>
        ))}
      </div>

      {/* Content Sections */}
      <div className="bg-zinc-900 rounded-lg p-6">
        {/* DOCUMENTI FISCALI ATTIVI */}
        {activeSection === "documenti" && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-white">Documenti Fiscali (Emessi)</h2>
              <Button onClick={fetchDocumentiAttivi} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4 mr-2" />
                Aggiorna
              </Button>
            </div>
            
            {/* Date Range Filter */}
            <DateRangeFilter
              startDate={dateFilters.documenti.start}
              endDate={dateFilters.documenti.end}
              onStartChange={(v) => updateDateFilter('documenti', 'start', v)}
              onEndChange={(v) => updateDateFilter('documenti', 'end', v)}
              onDownload={() => {
                const start = dateFilters.documenti.start;
                const end = dateFilters.documenti.end;
                downloadExcel(
                  `documenti-attivi?year=${selectedYear}`,
                  `Documenti_Fiscali_${start}_${end}.xlsx`,
                  start,
                  end
                );
              }}
              downloadLabel="Scarica Excel"
            />
            
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Numero</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Imponibile</TableHead>
                  <TableHead className="text-right">IVA</TableHead>
                  <TableHead className="text-right">Totale</TableHead>
                  <TableHead>Stato</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documentiAttivi.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-zinc-500 py-8">
                      Nessun documento trovato per il periodo selezionato
                    </TableCell>
                  </TableRow>
                ) : documentiAttivi.map(doc => (
                  <TableRow key={doc.id}>
                    <TableCell className="font-mono">{doc.numero}</TableCell>
                    <TableCell>{formatDate(doc.data)}</TableCell>
                    <TableCell>{doc.cliente}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{doc.tipo_documento || "FPR12"}</Badge>
                    </TableCell>
                    <TableCell className="text-right">{formatCurrency(doc.imponibile)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(doc.iva)}</TableCell>
                    <TableCell className="text-right font-semibold">{formatCurrency(doc.totale)}</TableCell>
                    <TableCell>
                      <Badge className={doc.stato === "pagata" ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}>
                        {doc.stato || "Emessa"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {documentiAttivi.length > 0 && (
              <div className="flex justify-end gap-4 pt-4 border-t border-zinc-800">
                <div className="text-zinc-400">
                  Totale Imponibile: <span className="text-white font-semibold">
                    {formatCurrency(documentiAttivi.reduce((sum, d) => sum + (d.imponibile || 0), 0))}
                  </span>
                </div>
                <div className="text-zinc-400">
                  Totale IVA: <span className="text-white font-semibold">
                    {formatCurrency(documentiAttivi.reduce((sum, d) => sum + (d.iva || 0), 0))}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* DOCUMENTI FISCALI PASSIVI */}
        {activeSection === "passivi" && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-white">Documenti Fiscali Passivi (Ricevuti)</h2>
              <Button onClick={() => { resetDocPassivoForm(); setEditingItem(null); setShowDocPassivoDialog(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Registra Fattura
              </Button>
            </div>
            
            {/* Date Range Filter */}
            <DateRangeFilter
              startDate={dateFilters.passivi.start}
              endDate={dateFilters.passivi.end}
              onStartChange={(v) => updateDateFilter('passivi', 'start', v)}
              onEndChange={(v) => updateDateFilter('passivi', 'end', v)}
              onDownload={() => {
                const start = dateFilters.passivi.start;
                const end = dateFilters.passivi.end;
                downloadExcel(
                  `documenti-passivi?year=${selectedYear}`,
                  `Documenti_Passivi_${start}_${end}.xlsx`,
                  start,
                  end
                );
              }}
              downloadLabel="Scarica Excel"
            />
            
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>N. Documento</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Fornitore</TableHead>
                  <TableHead>P.IVA</TableHead>
                  <TableHead className="text-right">Imponibile</TableHead>
                  <TableHead className="text-right">IVA</TableHead>
                  <TableHead className="text-right">Totale</TableHead>
                  <TableHead>Scadenza</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documentiPassivi.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-zinc-500 py-8">
                      Nessun documento passivo registrato
                    </TableCell>
                  </TableRow>
                ) : documentiPassivi.map(doc => (
                  <TableRow key={doc.id}>
                    <TableCell className="font-mono">{doc.numero_documento}</TableCell>
                    <TableCell>{formatDate(doc.data_documento)}</TableCell>
                    <TableCell>{doc.fornitore}</TableCell>
                    <TableCell className="font-mono text-sm">{doc.partita_iva_fornitore}</TableCell>
                    <TableCell className="text-right">{formatCurrency(doc.imponibile)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(doc.iva)}</TableCell>
                    <TableCell className="text-right font-semibold">{formatCurrency(doc.totale)}</TableCell>
                    <TableCell>{formatDate(doc.data_scadenza)}</TableCell>
                    <TableCell>
                      <Badge className={doc.stato_pagamento === "pagata" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                        {doc.stato_pagamento === "pagata" ? "Pagata" : "Da Pagare"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => {
                          setEditingItem(doc);
                          setDocPassivoForm(doc);
                          setShowDocPassivoDialog(true);
                        }}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete("documenti-passivi", doc.id)}>
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* AUTOFATTURE */}
        {activeSection === "autofatture" && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold text-white">Autofatture di Integrazione</h2>
                <p className="text-sm text-zinc-400">TD17 (Servizi UE), TD18 (Beni UE), TD19 (Beni Extra-UE)</p>
              </div>
              <Button onClick={() => { resetAutofatturaForm(); setEditingItem(null); setShowAutofatturaDialog(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Nuova Autofattura
              </Button>
            </div>
            
            {/* Date Range Filter */}
            <DateRangeFilter
              startDate={dateFilters.autofatture.start}
              endDate={dateFilters.autofatture.end}
              onStartChange={(v) => updateDateFilter('autofatture', 'start', v)}
              onEndChange={(v) => updateDateFilter('autofatture', 'end', v)}
              onDownload={() => {
                const start = dateFilters.autofatture.start;
                const end = dateFilters.autofatture.end;
                downloadExcel(
                  `autofatture?year=${selectedYear}`,
                  `Autofatture_${start}_${end}.xlsx`,
                  start,
                  end
                );
              }}
              downloadLabel="Scarica Excel"
            />
            
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Numero</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Fornitore Estero</TableHead>
                  <TableHead>Paese</TableHead>
                  <TableHead className="text-right">Imponibile</TableHead>
                  <TableHead className="text-right">IVA</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {autofatture.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-zinc-500 py-8">
                      Nessuna autofattura registrata
                    </TableCell>
                  </TableRow>
                ) : autofatture.map(doc => (
                  <TableRow key={doc.id}>
                    <TableCell>
                      <Badge variant="outline">{doc.tipo_documento}</Badge>
                    </TableCell>
                    <TableCell className="font-mono">{doc.numero_documento}</TableCell>
                    <TableCell>{formatDate(doc.data_documento)}</TableCell>
                    <TableCell>{doc.fornitore_estero}</TableCell>
                    <TableCell>{doc.paese}</TableCell>
                    <TableCell className="text-right">{formatCurrency(doc.imponibile)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(doc.iva)}</TableCell>
                    <TableCell>
                      <Badge className={doc.inviata_sdi ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}>
                        {doc.inviata_sdi ? "Inviata SDI" : "Da Inviare"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => {
                          setEditingItem(doc);
                          setAutofatturaForm(doc);
                          setShowAutofatturaDialog(true);
                        }}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete("autofatture", doc.id)}>
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* SCADENZARIO */}
        {activeSection === "scadenzario" && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-white">Scadenzario</h2>
              <Button onClick={() => { resetScadenzaForm(); setEditingItem(null); setShowScadenzaDialog(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Nuova Scadenza
              </Button>
            </div>
            
            {/* Date Range Filter */}
            <DateRangeFilter
              startDate={dateFilters.scadenzario.start}
              endDate={dateFilters.scadenzario.end}
              onStartChange={(v) => updateDateFilter('scadenzario', 'start', v)}
              onEndChange={(v) => updateDateFilter('scadenzario', 'end', v)}
              onDownload={() => {
                const start = dateFilters.scadenzario.start;
                const end = dateFilters.scadenzario.end;
                downloadExcel(
                  `scadenzario?year=${selectedYear}`,
                  `Scadenzario_${start}_${end}.xlsx`,
                  start,
                  end
                );
              }}
              downloadLabel="Scarica Excel"
            />
            
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-zinc-800 border-zinc-700">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-500/20 rounded-lg">
                      <AlertCircle className="w-5 h-5 text-red-400" />
                    </div>
                    <div>
                      <p className="text-sm text-zinc-400">Scadute</p>
                      <p className="text-xl font-bold text-red-400">
                        {formatCurrency(scadenze.filter(s => s.stato === "scaduta").reduce((sum, s) => sum + s.importo, 0))}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-zinc-800 border-zinc-700">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-yellow-500/20 rounded-lg">
                      <Clock className="w-5 h-5 text-yellow-400" />
                    </div>
                    <div>
                      <p className="text-sm text-zinc-400">In Scadenza</p>
                      <p className="text-xl font-bold text-yellow-400">
                        {formatCurrency(scadenze.filter(s => s.stato === "aperta").reduce((sum, s) => sum + s.importo, 0))}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-zinc-800 border-zinc-700">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-500/20 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-sm text-zinc-400">Pagate</p>
                      <p className="text-xl font-bold text-green-400">
                        {formatCurrency(scadenze.filter(s => s.stato === "pagata").reduce((sum, s) => sum + s.importo, 0))}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Descrizione</TableHead>
                  <TableHead>Cliente/Fornitore</TableHead>
                  <TableHead>Documento</TableHead>
                  <TableHead>Scadenza</TableHead>
                  <TableHead className="text-right">Importo</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scadenze.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-zinc-500 py-8">
                      Nessuna scadenza per il periodo selezionato
                    </TableCell>
                  </TableRow>
                ) : scadenze.map(scad => (
                  <TableRow key={scad.id} className={scad.stato === "scaduta" ? "bg-red-500/5" : ""}>
                    <TableCell>
                      <Badge variant="outline" className={scad.tipo === "incasso" ? "border-green-500 text-green-400" : "border-red-500 text-red-400"}>
                        {scad.tipo === "incasso" ? "Incasso" : "Pagamento"}
                      </Badge>
                    </TableCell>
                    <TableCell>{scad.descrizione}</TableCell>
                    <TableCell>{scad.cliente_fornitore}</TableCell>
                    <TableCell className="font-mono text-sm">{scad.documento_riferimento}</TableCell>
                    <TableCell>{formatDate(scad.data_scadenza)}</TableCell>
                    <TableCell className="text-right font-semibold">{formatCurrency(scad.importo)}</TableCell>
                    <TableCell>
                      <Badge className={
                        scad.stato === "pagata" ? "bg-green-500/20 text-green-400" :
                        scad.stato === "scaduta" ? "bg-red-500/20 text-red-400" :
                        "bg-yellow-500/20 text-yellow-400"
                      }>
                        {scad.stato === "pagata" ? "Pagata" : scad.stato === "scaduta" ? "Scaduta" : "Aperta"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {scad.stato !== "pagata" && (
                          <Button variant="ghost" size="icon" onClick={() => handleMarkScadenzaPagata(scad.id)} title="Segna come pagata">
                            <Check className="w-4 h-4 text-green-400" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => {
                          setEditingItem(scad);
                          setScadenzaForm(scad);
                          setShowScadenzaDialog(true);
                        }}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete("scadenzario", scad.id)}>
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* RIEPILOGHI FISCALI */}
        {activeSection === "riepiloghi" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Riepiloghi Fiscali {selectedYear}</h2>
            </div>
            
            {/* Date Range Filter */}
            <DateRangeFilter
              startDate={dateFilters.riepiloghi.start}
              endDate={dateFilters.riepiloghi.end}
              onStartChange={(v) => updateDateFilter('riepiloghi', 'start', v)}
              onEndChange={(v) => updateDateFilter('riepiloghi', 'end', v)}
              onDownload={() => {
                const start = dateFilters.riepiloghi.start;
                const end = dateFilters.riepiloghi.end;
                downloadExcel(
                  `riepiloghi?year=${selectedYear}`,
                  `Riepiloghi_Fiscali_${start}_${end}.xlsx`,
                  start,
                  end
                );
              }}
              downloadLabel="Scarica Excel"
            />
            
            {riepiloghi ? (
              <>
                {/* Annual Summary */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="bg-zinc-800 border-zinc-700">
                    <CardContent className="p-4">
                      <p className="text-sm text-zinc-400">Totale Fatturato</p>
                      <p className="text-2xl font-bold text-white">{formatCurrency(riepiloghi.totale_fatturato)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-zinc-800 border-zinc-700">
                    <CardContent className="p-4">
                      <p className="text-sm text-zinc-400">IVA a Debito</p>
                      <p className="text-2xl font-bold text-red-400">{formatCurrency(riepiloghi.iva_debito)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-zinc-800 border-zinc-700">
                    <CardContent className="p-4">
                      <p className="text-sm text-zinc-400">IVA a Credito</p>
                      <p className="text-2xl font-bold text-green-400">{formatCurrency(riepiloghi.iva_credito)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-zinc-800 border-zinc-700">
                    <CardContent className="p-4">
                      <p className="text-sm text-zinc-400">Saldo IVA</p>
                      <p className={`text-2xl font-bold ${riepiloghi.saldo_iva >= 0 ? "text-red-400" : "text-green-400"}`}>
                        {formatCurrency(Math.abs(riepiloghi.saldo_iva))} {riepiloghi.saldo_iva >= 0 ? "(Debito)" : "(Credito)"}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Quarterly Breakdown */}
                <Card className="bg-zinc-800 border-zinc-700">
                  <CardHeader>
                    <CardTitle className="text-white">Liquidazioni Trimestrali</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Trimestre</TableHead>
                          <TableHead className="text-right">IVA Vendite</TableHead>
                          <TableHead className="text-right">IVA Acquisti</TableHead>
                          <TableHead className="text-right">Saldo</TableHead>
                          <TableHead>Stato</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(riepiloghi.trimestri || []).map((t, i) => (
                          <TableRow key={i}>
                            <TableCell>Q{i + 1} {selectedYear}</TableCell>
                            <TableCell className="text-right">{formatCurrency(t.iva_vendite)}</TableCell>
                            <TableCell className="text-right">{formatCurrency(t.iva_acquisti)}</TableCell>
                            <TableCell className={`text-right font-semibold ${t.saldo >= 0 ? "text-red-400" : "text-green-400"}`}>
                              {formatCurrency(Math.abs(t.saldo))}
                            </TableCell>
                            <TableCell>
                              <Badge className={t.versata ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}>
                                {t.versata ? "Versata" : "Da Versare"}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </>
            ) : (
              <div className="text-center py-8 text-zinc-500">
                Nessun dato disponibile per il periodo selezionato
              </div>
            )}
          </div>
        )}

        {/* PRIMA NOTA */}
        {activeSection === "prima-nota" && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-white">Registrazioni Prima Nota</h2>
              <Button onClick={() => { resetPrimaNotaForm(); setEditingItem(null); setShowPrimaNotaDialog(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Nuova Registrazione
              </Button>
            </div>
            
            {/* Date Range Filter */}
            <DateRangeFilter
              startDate={dateFilters.primaNota.start}
              endDate={dateFilters.primaNota.end}
              onStartChange={(v) => updateDateFilter('primaNota', 'start', v)}
              onEndChange={(v) => updateDateFilter('primaNota', 'end', v)}
              onDownload={() => {
                const start = dateFilters.primaNota.start;
                const end = dateFilters.primaNota.end;
                downloadExcel(
                  `prima-nota?year=${selectedYear}`,
                  `Prima_Nota_${start}_${end}.xlsx`,
                  start,
                  end
                );
              }}
              downloadLabel="Scarica Excel"
            />
            
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Descrizione</TableHead>
                  <TableHead>Conto Dare</TableHead>
                  <TableHead>Conto Avere</TableHead>
                  <TableHead className="text-right">Importo</TableHead>
                  <TableHead>Documento</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {primaNota.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-zinc-500 py-8">
                      Nessuna registrazione per il periodo selezionato
                    </TableCell>
                  </TableRow>
                ) : primaNota.map(reg => (
                  <TableRow key={reg.id}>
                    <TableCell>{formatDate(reg.data_registrazione)}</TableCell>
                    <TableCell>{reg.descrizione}</TableCell>
                    <TableCell className="font-mono text-sm">{reg.conto_dare}</TableCell>
                    <TableCell className="font-mono text-sm">{reg.conto_avere}</TableCell>
                    <TableCell className="text-right font-semibold">{formatCurrency(reg.importo)}</TableCell>
                    <TableCell className="font-mono text-sm">{reg.documento_riferimento}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => {
                          setEditingItem(reg);
                          setPrimaNotaForm(reg);
                          setShowPrimaNotaDialog(true);
                        }}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete("prima-nota", reg.id)}>
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* PIANO DEI CONTI */}
        {activeSection === "piano-conti" && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold text-white">Piano dei Conti</h2>
                <p className="text-sm text-zinc-400">I saldi sono calcolati automaticamente dalla Prima Nota</p>
              </div>
              <div className="flex gap-2">
                {pianoConti.length === 0 && (
                  <Button 
                    onClick={async () => {
                      try {
                        const res = await authFetch(`${API}/fiscal/piano-conti/inizializza`, {
                          method: "POST"
                        });
                        if (res.ok) {
                          toast.success("Piano dei Conti inizializzato con i conti standard");
                          fetchPianoConti();
                        } else {
                          toast.error("Errore nell'inizializzazione");
                        }
                      } catch (error) {
                        toast.error("Errore di connessione");
                      }
                    }}
                    variant="outline"
                    className="border-green-600 text-green-400 hover:bg-green-600/20"
                    data-testid="inizializza-piano-conti-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Inizializza Piano Standard
                  </Button>
                )}
                <Button 
                  onClick={async () => {
                    if (!window.confirm("Sei sicuro? Questa operazione eliminerà tutte le scritture esistenti e le rigenererà dalle fatture.")) return;
                    try {
                      const res = await authFetch(`${API}/fiscal/reset-scritture`, {
                        method: "POST"
                      });
                      if (res.ok) {
                        const data = await res.json();
                        toast.success(`Reset completato: ${data.scritture_generate} scritture generate`);
                        fetchPianoConti();
                        fetchPrimaNota();
                      } else {
                        toast.error("Errore nel reset");
                      }
                    } catch (error) {
                      toast.error("Errore di connessione");
                    }
                  }}
                  variant="outline"
                  className="border-red-600 text-red-400 hover:bg-red-600/20"
                  data-testid="reset-scritture-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reset Scritture
                </Button>
                <Button 
                  onClick={async () => {
                    try {
                      const res = await authFetch(`${API}/fiscal/sincronizza-scritture`, {
                        method: "POST"
                      });
                      if (res.ok) {
                        const data = await res.json();
                        toast.success(`Sincronizzate ${data.fatture_nuove_registrate} nuove fatture`);
                        fetchPianoConti();
                        fetchPrimaNota();
                      } else {
                        toast.error("Errore nella sincronizzazione");
                      }
                    } catch (error) {
                      toast.error("Errore di connessione");
                    }
                  }}
                  variant="outline"
                  className="border-blue-600 text-blue-400 hover:bg-blue-600/20"
                  data-testid="sincronizza-scritture-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Sincronizza Nuove
                </Button>
                <Button onClick={() => { resetContoForm(); setEditingItem(null); setShowContoDialog(true); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Nuovo Conto
                </Button>
              </div>
            </div>
            
            {/* Summary stats */}
            {pianoConti.length > 0 && (
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                  <p className="text-xs text-zinc-400 uppercase">Attivo</p>
                  <p className="text-lg font-bold text-blue-400">
                    {formatCurrency(pianoContiTotali.attivo)}
                  </p>
                </div>
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <p className="text-xs text-zinc-400 uppercase">Passivo</p>
                  <p className="text-lg font-bold text-red-400">
                    {formatCurrency(pianoContiTotali.passivo)}
                  </p>
                </div>
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                  <p className="text-xs text-zinc-400 uppercase">Ricavi</p>
                  <p className="text-lg font-bold text-green-400">
                    {formatCurrency(pianoContiTotali.ricavi)}
                  </p>
                </div>
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                  <p className="text-xs text-zinc-400 uppercase">Costi</p>
                  <p className="text-lg font-bold text-orange-400">
                    {formatCurrency(pianoContiTotali.costi)}
                  </p>
                </div>
              </div>
            )}
            
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Codice</TableHead>
                  <TableHead>Descrizione</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead className="text-right">Saldo</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pianoConti.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-zinc-500 py-8">
                      Nessun conto configurato
                    </TableCell>
                  </TableRow>
                ) : pianoConti.map(conto => (
                  <TableRow 
                    key={conto.id} 
                    className="cursor-pointer hover:bg-zinc-800/50"
                    onClick={() => fetchMovimentiConto(conto.id)}
                    data-testid={`conto-row-${conto.codice}`}
                  >
                    <TableCell className="font-mono">{conto.codice}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {conto.descrizione}
                        <Eye className="w-3 h-3 text-zinc-500" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{conto.tipo}</Badge>
                    </TableCell>
                    <TableCell>{conto.categoria}</TableCell>
                    <TableCell className={`text-right font-semibold ${conto.saldo >= 0 ? "" : "text-red-400"}`}>
                      {formatCurrency(conto.saldo)}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" onClick={() => {
                          setEditingItem(conto);
                          setContoForm(conto);
                          setShowContoDialog(true);
                        }}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete("piano-conti", conto.id)}>
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* GESTIONE FIDO */}
        {activeSection === "fido" && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-white">Gestione Fido Clienti</h2>
              <Button onClick={() => { resetFidoForm(); setEditingItem(null); setShowFidoDialog(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Fido
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead className="text-right">Fido Accordato</TableHead>
                  <TableHead className="text-right">Fido Utilizzato</TableHead>
                  <TableHead className="text-right">Disponibile</TableHead>
                  <TableHead>Utilizzo</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fido.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-zinc-500 py-8">
                      Nessun fido configurato
                    </TableCell>
                  </TableRow>
                ) : fido.map(f => {
                  const disponibile = f.fido_accordato - f.fido_utilizzato;
                  const percentuale = f.fido_accordato > 0 ? (f.fido_utilizzato / f.fido_accordato) * 100 : 0;
                  return (
                    <TableRow key={f.id}>
                      <TableCell className="font-semibold">{f.cliente_nome}</TableCell>
                      <TableCell className="text-right">{formatCurrency(f.fido_accordato)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(f.fido_utilizzato)}</TableCell>
                      <TableCell className={`text-right font-semibold ${disponibile < 0 ? "text-red-400" : "text-green-400"}`}>
                        {formatCurrency(disponibile)}
                      </TableCell>
                      <TableCell>
                        <div className="w-24 bg-zinc-700 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${percentuale > 90 ? "bg-red-500" : percentuale > 70 ? "bg-yellow-500" : "bg-green-500"}`}
                            style={{ width: `${Math.min(percentuale, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-zinc-400">{percentuale.toFixed(0)}%</span>
                      </TableCell>
                      <TableCell>
                        <Badge className={f.stato === "attivo" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                          {f.stato === "attivo" ? "Attivo" : "Sospeso"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => {
                            setEditingItem(f);
                            setFidoForm(f);
                            setShowFidoDialog(true);
                          }}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete("fido", f.id)}>
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {/* ESTRATTO CONTO */}
        {activeSection === "estratto-conto" && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-white">Estratto Conto</h2>
              <Button onClick={fetchEstrattoConto} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4 mr-2" />
                Aggiorna
              </Button>
            </div>
            
            {/* Date Range Filter */}
            <DateRangeFilter
              startDate={dateFilters.estrattoConto.start}
              endDate={dateFilters.estrattoConto.end}
              onStartChange={(v) => updateDateFilter('estrattoConto', 'start', v)}
              onEndChange={(v) => updateDateFilter('estrattoConto', 'end', v)}
              onDownload={() => {
                const start = dateFilters.estrattoConto.start;
                const end = dateFilters.estrattoConto.end;
                downloadExcel(
                  `estratto-conto?year=${selectedYear}&month=${selectedMonth}`,
                  `Estratto_Conto_${start}_${end}.xlsx`,
                  start,
                  end
                );
              }}
              downloadLabel="Scarica Excel"
            />
            
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Descrizione</TableHead>
                  <TableHead>Documento</TableHead>
                  <TableHead className="text-right">Dare</TableHead>
                  <TableHead className="text-right">Avere</TableHead>
                  <TableHead className="text-right">Saldo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {estrattoConto.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-zinc-500 py-8">
                      Nessun movimento per il periodo selezionato
                    </TableCell>
                  </TableRow>
                ) : estrattoConto.map((mov, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{formatDate(mov.data)}</TableCell>
                    <TableCell>{mov.descrizione}</TableCell>
                    <TableCell className="font-mono text-sm">{mov.documento}</TableCell>
                    <TableCell className="text-right text-red-400">{mov.dare > 0 ? formatCurrency(mov.dare) : "-"}</TableCell>
                    <TableCell className="text-right text-green-400">{mov.avere > 0 ? formatCurrency(mov.avere) : "-"}</TableCell>
                    <TableCell className={`text-right font-semibold ${mov.saldo >= 0 ? "" : "text-red-400"}`}>
                      {formatCurrency(mov.saldo)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* REGISTRO IVA */}
        {activeSection === "registro-iva" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Registro IVA {selectedYear}</h2>
            </div>
            
            {/* Date Range Filter */}
            <DateRangeFilter
              startDate={dateFilters.registroIva.start}
              endDate={dateFilters.registroIva.end}
              onStartChange={(v) => updateDateFilter('registroIva', 'start', v)}
              onEndChange={(v) => updateDateFilter('registroIva', 'end', v)}
              onDownload={() => {
                const start = dateFilters.registroIva.start;
                const end = dateFilters.registroIva.end;
                // Download both vendite and acquisti
                downloadExcel(
                  `registro-iva/vendite?year=${selectedYear}`,
                  `Registro_IVA_Vendite_${start}_${end}.xlsx`,
                  start,
                  end
                );
                setTimeout(() => {
                  downloadExcel(
                    `registro-iva/acquisti?year=${selectedYear}`,
                    `Registro_IVA_Acquisti_${start}_${end}.xlsx`,
                    start,
                    end
                  );
                }, 500);
              }}
              downloadLabel="Scarica Vendite + Acquisti"
            />
            
            <Tabs defaultValue="vendite" className="w-full">
              <TabsList className="bg-zinc-800">
                <TabsTrigger value="vendite">Registro Vendite</TabsTrigger>
                <TabsTrigger value="acquisti">Registro Acquisti</TabsTrigger>
              </TabsList>
              
              <TabsContent value="vendite" className="mt-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>N. Reg.</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>N. Documento</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead className="text-right">Imponibile</TableHead>
                      <TableHead className="text-right">Aliquota</TableHead>
                      <TableHead className="text-right">IVA</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {registroIva.vendite.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-zinc-500 py-8">
                          Nessuna registrazione vendite
                        </TableCell>
                      </TableRow>
                    ) : registroIva.vendite.map((reg, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{formatDate(reg.data)}</TableCell>
                        <TableCell className="font-mono">{reg.numero_documento}</TableCell>
                        <TableCell>{reg.cliente}</TableCell>
                        <TableCell className="text-right">{formatCurrency(reg.imponibile)}</TableCell>
                        <TableCell className="text-right">{reg.aliquota}%</TableCell>
                        <TableCell className="text-right font-semibold">{formatCurrency(reg.iva)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {registroIva.vendite.length > 0 && (
                  <div className="flex justify-end gap-4 pt-4 border-t border-zinc-800 mt-4">
                    <span className="text-zinc-400">Totale IVA Vendite: <span className="text-white font-bold">
                      {formatCurrency(registroIva.vendite.reduce((sum, r) => sum + (r.iva || 0), 0))}
                    </span></span>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="acquisti" className="mt-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>N. Reg.</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>N. Documento</TableHead>
                      <TableHead>Fornitore</TableHead>
                      <TableHead className="text-right">Imponibile</TableHead>
                      <TableHead className="text-right">Aliquota</TableHead>
                      <TableHead className="text-right">IVA</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {registroIva.acquisti.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-zinc-500 py-8">
                          Nessuna registrazione acquisti
                        </TableCell>
                      </TableRow>
                    ) : registroIva.acquisti.map((reg, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{formatDate(reg.data)}</TableCell>
                        <TableCell className="font-mono">{reg.numero_documento}</TableCell>
                        <TableCell>{reg.fornitore}</TableCell>
                        <TableCell className="text-right">{formatCurrency(reg.imponibile)}</TableCell>
                        <TableCell className="text-right">{reg.aliquota}%</TableCell>
                        <TableCell className="text-right font-semibold">{formatCurrency(reg.iva)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {registroIva.acquisti.length > 0 && (
                  <div className="flex justify-end gap-4 pt-4 border-t border-zinc-800 mt-4">
                    <span className="text-zinc-400">Totale IVA Acquisti: <span className="text-white font-bold">
                      {formatCurrency(registroIva.acquisti.reduce((sum, r) => sum + (r.iva || 0), 0))}
                    </span></span>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}

        {/* PAGAMENTI E INCASSI */}
        {activeSection === "pagamenti" && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-white">Pagamenti e Incassi</h2>
              <Button onClick={fetchPagamentiIncassi} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4 mr-2" />
                Aggiorna
              </Button>
            </div>
            
            {/* Date Range Filter */}
            <DateRangeFilter
              startDate={dateFilters.pagamenti.start}
              endDate={dateFilters.pagamenti.end}
              onStartChange={(v) => updateDateFilter('pagamenti', 'start', v)}
              onEndChange={(v) => updateDateFilter('pagamenti', 'end', v)}
              onDownload={() => {
                const start = dateFilters.pagamenti.start;
                const end = dateFilters.pagamenti.end;
                downloadExcel(
                  `pagamenti-incassi?year=${selectedYear}&month=${selectedMonth}`,
                  `Pagamenti_Incassi_${start}_${end}.xlsx`,
                  start,
                  end
                );
              }}
              downloadLabel="Scarica Excel"
            />
            
            {/* Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-zinc-800 border-zinc-700">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <ArrowDownRight className="w-8 h-8 text-green-400" />
                    <div>
                      <p className="text-sm text-zinc-400">Totale Incassi</p>
                      <p className="text-xl font-bold text-green-400">
                        {formatCurrency(pagamentiIncassi.totale_incassi || 0)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-zinc-800 border-zinc-700">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <ArrowUpRight className="w-8 h-8 text-red-400" />
                    <div>
                      <p className="text-sm text-zinc-400">Totale Pagamenti</p>
                      <p className="text-xl font-bold text-red-400">
                        {formatCurrency(pagamentiIncassi.totale_pagamenti || 0)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-zinc-800 border-zinc-700">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Euro className="w-8 h-8 text-primary" />
                    <div>
                      <p className="text-sm text-zinc-400">Saldo</p>
                      <p className="text-xl font-bold text-white">
                        {formatCurrency(pagamentiIncassi.saldo || 0)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Descrizione</TableHead>
                  <TableHead>Cliente/Fornitore</TableHead>
                  <TableHead>Metodo</TableHead>
                  <TableHead className="text-right">Importo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(!pagamentiIncassi.movimenti || pagamentiIncassi.movimenti.length === 0) ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-zinc-500 py-8">
                      Nessun movimento per il periodo selezionato
                    </TableCell>
                  </TableRow>
                ) : pagamentiIncassi.movimenti.map((mov, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{formatDate(mov.data)}</TableCell>
                    <TableCell>
                      <Badge className={mov.tipo === "incasso" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                        {mov.tipo === "incasso" ? "Incasso" : "Pagamento"}
                      </Badge>
                    </TableCell>
                    <TableCell>{mov.descrizione}</TableCell>
                    <TableCell>{mov.controparte}</TableCell>
                    <TableCell>{mov.metodo}</TableCell>
                    <TableCell className={`text-right font-semibold ${mov.tipo === "incasso" ? "text-green-400" : "text-red-400"}`}>
                      {mov.tipo === "incasso" ? "+" : "-"}{formatCurrency(mov.importo)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* DIALOGS */}
      
      {/* Dialog Documento Passivo */}
      <Dialog open={showDocPassivoDialog} onOpenChange={setShowDocPassivoDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Modifica Documento Passivo" : "Registra Fattura Fornitore"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-zinc-400">Numero Documento</label>
              <Input 
                value={docPassivoForm.numero_documento}
                onChange={(e) => setDocPassivoForm({...docPassivoForm, numero_documento: e.target.value})}
                placeholder="Es. 123/2026"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400">Data Documento</label>
              <Input 
                type="date"
                value={docPassivoForm.data_documento}
                onChange={(e) => setDocPassivoForm({...docPassivoForm, data_documento: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400">Fornitore</label>
              <Input 
                value={docPassivoForm.fornitore}
                onChange={(e) => setDocPassivoForm({...docPassivoForm, fornitore: e.target.value})}
                placeholder="Ragione sociale"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400">P.IVA Fornitore</label>
              <Input 
                value={docPassivoForm.partita_iva_fornitore}
                onChange={(e) => setDocPassivoForm({...docPassivoForm, partita_iva_fornitore: e.target.value})}
                placeholder="IT12345678901"
              />
            </div>
            <div className="col-span-2">
              <label className="text-sm text-zinc-400">Descrizione</label>
              <Textarea 
                value={docPassivoForm.descrizione}
                onChange={(e) => setDocPassivoForm({...docPassivoForm, descrizione: e.target.value})}
                placeholder="Descrizione della fornitura"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400">Imponibile</label>
              <Input 
                type="number"
                step="0.01"
                value={docPassivoForm.imponibile}
                onChange={(e) => setDocPassivoForm({...docPassivoForm, imponibile: parseFloat(e.target.value) || 0})}
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400">Aliquota IVA %</label>
              <Select value={docPassivoForm.aliquota_iva.toString()} onValueChange={(v) => setDocPassivoForm({...docPassivoForm, aliquota_iva: parseInt(v), natura_iva: parseInt(v) === 0 ? docPassivoForm.natura_iva : ""})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="22">22%</SelectItem>
                  <SelectItem value="10">10%</SelectItem>
                  <SelectItem value="4">4%</SelectItem>
                  <SelectItem value="0">0% (Esente/RC)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Mostra Natura IVA solo se aliquota è 0% */}
            {docPassivoForm.aliquota_iva === 0 && (
              <div>
                <label className="text-sm text-zinc-400">Natura IVA (obbligatoria)</label>
                <Select value={docPassivoForm.natura_iva} onValueChange={(v) => setDocPassivoForm({...docPassivoForm, natura_iva: v})}>
                  <SelectTrigger className={!docPassivoForm.natura_iva ? "border-orange-500" : ""}>
                    <SelectValue placeholder="Seleziona Natura" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {NATURE_IVA.map((n) => (
                      <SelectItem key={n.code} value={n.code || "empty"}>{n.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!docPassivoForm.natura_iva && (
                  <p className="text-xs text-orange-400 mt-1">Obbligatoria per IVA 0%</p>
                )}
              </div>
            )}
            <div>
              <label className="text-sm text-zinc-400">IVA</label>
              <Input type="number" value={docPassivoForm.iva} disabled className="bg-zinc-800" />
            </div>
            <div>
              <label className="text-sm text-zinc-400">Totale</label>
              <Input type="number" value={docPassivoForm.totale} disabled className="bg-zinc-800 font-bold" />
            </div>
            <div>
              <label className="text-sm text-zinc-400">Data Scadenza</label>
              <Input 
                type="date"
                value={docPassivoForm.data_scadenza}
                onChange={(e) => setDocPassivoForm({...docPassivoForm, data_scadenza: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400">Stato Pagamento</label>
              <Select value={docPassivoForm.stato_pagamento} onValueChange={(v) => setDocPassivoForm({...docPassivoForm, stato_pagamento: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="da_pagare">Da Pagare</SelectItem>
                  <SelectItem value="pagata">Pagata</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocPassivoDialog(false)}>Annulla</Button>
            <Button onClick={handleSaveDocPassivo}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Autofattura */}
      <Dialog open={showAutofatturaDialog} onOpenChange={setShowAutofatturaDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Modifica Autofattura" : "Nuova Autofattura"}</DialogTitle>
            <DialogDescription>Autofattura per acquisti intracomunitari o extra-UE</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-zinc-400">Tipo Documento</label>
              <Select value={autofatturaForm.tipo_documento} onValueChange={(v) => setAutofatturaForm({...autofatturaForm, tipo_documento: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TD17">TD17 - Servizi UE</SelectItem>
                  <SelectItem value="TD18">TD18 - Beni UE</SelectItem>
                  <SelectItem value="TD19">TD19 - Beni Extra-UE</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-zinc-400">Numero Documento</label>
              <Input 
                value={autofatturaForm.numero_documento}
                onChange={(e) => setAutofatturaForm({...autofatturaForm, numero_documento: e.target.value})}
                placeholder="Es. AF001/2026"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400">Data Documento</label>
              <Input 
                type="date"
                value={autofatturaForm.data_documento}
                onChange={(e) => setAutofatturaForm({...autofatturaForm, data_documento: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400">Paese Fornitore</label>
              <Input 
                value={autofatturaForm.paese}
                onChange={(e) => setAutofatturaForm({...autofatturaForm, paese: e.target.value})}
                placeholder="Es. DE, FR, US"
              />
            </div>
            <div className="col-span-2">
              <label className="text-sm text-zinc-400">Fornitore Estero</label>
              <Input 
                value={autofatturaForm.fornitore_estero}
                onChange={(e) => setAutofatturaForm({...autofatturaForm, fornitore_estero: e.target.value})}
                placeholder="Ragione sociale fornitore estero"
              />
            </div>
            <div className="col-span-2">
              <label className="text-sm text-zinc-400">Descrizione</label>
              <Textarea 
                value={autofatturaForm.descrizione}
                onChange={(e) => setAutofatturaForm({...autofatturaForm, descrizione: e.target.value})}
                placeholder="Descrizione dell'acquisto"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400">Imponibile</label>
              <Input 
                type="number"
                step="0.01"
                value={autofatturaForm.imponibile}
                onChange={(e) => setAutofatturaForm({...autofatturaForm, imponibile: parseFloat(e.target.value) || 0})}
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400">Aliquota IVA %</label>
              <Select value={autofatturaForm.aliquota_iva.toString()} onValueChange={(v) => setAutofatturaForm({...autofatturaForm, aliquota_iva: parseInt(v), natura_iva: parseInt(v) === 0 ? autofatturaForm.natura_iva : ""})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="22">22%</SelectItem>
                  <SelectItem value="10">10%</SelectItem>
                  <SelectItem value="4">4%</SelectItem>
                  <SelectItem value="0">0% (Reverse Charge)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Mostra Natura IVA solo se aliquota è 0% - Per autofatture tipicamente N6.x */}
            {autofatturaForm.aliquota_iva === 0 && (
              <div className="col-span-2">
                <label className="text-sm text-zinc-400">Natura IVA (obbligatoria per RC)</label>
                <Select value={autofatturaForm.natura_iva} onValueChange={(v) => setAutofatturaForm({...autofatturaForm, natura_iva: v})}>
                  <SelectTrigger className={!autofatturaForm.natura_iva ? "border-orange-500" : ""}>
                    <SelectValue placeholder="Seleziona Natura - Per autofatture usare N6.x" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {NATURE_IVA.filter(n => n.code.startsWith('N6') || n.code === '' || n.code === 'N7').map((n) => (
                      <SelectItem key={n.code} value={n.code || "empty"}>{n.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!autofatturaForm.natura_iva && (
                  <p className="text-xs text-orange-400 mt-1">Per autofatture usare codici N6.x (Reverse Charge)</p>
                )}
              </div>
            )}
            <div className="col-span-2">
              <label className="text-sm text-zinc-400">IVA da integrare</label>
              <Input type="number" value={autofatturaForm.iva} disabled className="bg-zinc-800 font-bold" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAutofatturaDialog(false)}>Annulla</Button>
            <Button onClick={handleSaveAutofattura}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Scadenza */}
      <Dialog open={showScadenzaDialog} onOpenChange={setShowScadenzaDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingItem ? "Modifica Scadenza" : "Nuova Scadenza"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-zinc-400">Tipo</label>
              <Select value={scadenzaForm.tipo} onValueChange={(v) => setScadenzaForm({...scadenzaForm, tipo: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pagamento">Pagamento (Uscita)</SelectItem>
                  <SelectItem value="incasso">Incasso (Entrata)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-zinc-400">Descrizione</label>
              <Input 
                value={scadenzaForm.descrizione}
                onChange={(e) => setScadenzaForm({...scadenzaForm, descrizione: e.target.value})}
                placeholder="Es. Pagamento fattura fornitore"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400">Cliente/Fornitore</label>
              <Input 
                value={scadenzaForm.cliente_fornitore}
                onChange={(e) => setScadenzaForm({...scadenzaForm, cliente_fornitore: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400">Documento di Riferimento</label>
              <Input 
                value={scadenzaForm.documento_riferimento}
                onChange={(e) => setScadenzaForm({...scadenzaForm, documento_riferimento: e.target.value})}
                placeholder="Es. FT-001/2026"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400">Importo</label>
              <Input 
                type="number"
                step="0.01"
                value={scadenzaForm.importo}
                onChange={(e) => setScadenzaForm({...scadenzaForm, importo: parseFloat(e.target.value) || 0})}
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400">Data Scadenza</label>
              <Input 
                type="date"
                value={scadenzaForm.data_scadenza}
                onChange={(e) => setScadenzaForm({...scadenzaForm, data_scadenza: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScadenzaDialog(false)}>Annulla</Button>
            <Button onClick={handleSaveScadenza}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Prima Nota */}
      <Dialog open={showPrimaNotaDialog} onOpenChange={setShowPrimaNotaDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingItem ? "Modifica Registrazione" : "Nuova Registrazione Prima Nota"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-zinc-400">Data Registrazione</label>
              <Input 
                type="date"
                value={primaNotaForm.data_registrazione}
                onChange={(e) => setPrimaNotaForm({...primaNotaForm, data_registrazione: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400">Descrizione</label>
              <Input 
                value={primaNotaForm.descrizione}
                onChange={(e) => setPrimaNotaForm({...primaNotaForm, descrizione: e.target.value})}
                placeholder="Causale della registrazione"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400">Conto Dare</label>
              <Input 
                value={primaNotaForm.conto_dare}
                onChange={(e) => setPrimaNotaForm({...primaNotaForm, conto_dare: e.target.value})}
                placeholder="Es. Cassa, Banca, Clienti"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400">Conto Avere</label>
              <Input 
                value={primaNotaForm.conto_avere}
                onChange={(e) => setPrimaNotaForm({...primaNotaForm, conto_avere: e.target.value})}
                placeholder="Es. Ricavi, Fornitori"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400">Importo</label>
              <Input 
                type="number"
                step="0.01"
                value={primaNotaForm.importo}
                onChange={(e) => setPrimaNotaForm({...primaNotaForm, importo: parseFloat(e.target.value) || 0})}
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400">Documento Riferimento</label>
              <Input 
                value={primaNotaForm.documento_riferimento}
                onChange={(e) => setPrimaNotaForm({...primaNotaForm, documento_riferimento: e.target.value})}
                placeholder="Es. FT-001/2026"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPrimaNotaDialog(false)}>Annulla</Button>
            <Button onClick={handleSavePrimaNota}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Piano Conti */}
      <Dialog open={showContoDialog} onOpenChange={setShowContoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingItem ? "Modifica Conto" : "Nuovo Conto"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-zinc-400">Codice</label>
              <Input 
                value={contoForm.codice}
                onChange={(e) => setContoForm({...contoForm, codice: e.target.value})}
                placeholder="Es. 101, 201, 301"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400">Descrizione</label>
              <Input 
                value={contoForm.descrizione}
                onChange={(e) => setContoForm({...contoForm, descrizione: e.target.value})}
                placeholder="Nome del conto"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400">Tipo</label>
              <Select value={contoForm.tipo} onValueChange={(v) => setContoForm({...contoForm, tipo: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="patrimoniale">Patrimoniale</SelectItem>
                  <SelectItem value="economico">Economico</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-zinc-400">Categoria</label>
              <Input 
                value={contoForm.categoria}
                onChange={(e) => setContoForm({...contoForm, categoria: e.target.value})}
                placeholder="Es. Attivo, Passivo, Costi, Ricavi"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400">Saldo Iniziale</label>
              <Input 
                type="number"
                step="0.01"
                value={contoForm.saldo_iniziale}
                onChange={(e) => setContoForm({...contoForm, saldo_iniziale: parseFloat(e.target.value) || 0})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContoDialog(false)}>Annulla</Button>
            <Button onClick={handleSaveConto}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Fido */}
      <Dialog open={showFidoDialog} onOpenChange={setShowFidoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingItem ? "Modifica Fido" : "Nuovo Fido Cliente"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-zinc-400">Nome Cliente</label>
              <Input 
                value={fidoForm.cliente_nome}
                onChange={(e) => setFidoForm({...fidoForm, cliente_nome: e.target.value})}
                placeholder="Ragione sociale cliente"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400">Fido Accordato</label>
              <Input 
                type="number"
                step="0.01"
                value={fidoForm.fido_accordato}
                onChange={(e) => setFidoForm({...fidoForm, fido_accordato: parseFloat(e.target.value) || 0})}
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400">Fido Utilizzato</label>
              <Input 
                type="number"
                step="0.01"
                value={fidoForm.fido_utilizzato}
                onChange={(e) => setFidoForm({...fidoForm, fido_utilizzato: parseFloat(e.target.value) || 0})}
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400">Stato</label>
              <Select value={fidoForm.stato} onValueChange={(v) => setFidoForm({...fidoForm, stato: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="attivo">Attivo</SelectItem>
                  <SelectItem value="sospeso">Sospeso</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFidoDialog(false)}>Annulla</Button>
            <Button onClick={handleSaveFido}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Movimenti Conto */}
      <Dialog open={showMovimentiContoDialog} onOpenChange={setShowMovimentiContoDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Book className="w-5 h-5 text-primary" />
              Movimenti Conto: {selectedContoMovimenti?.conto?.codice} - {selectedContoMovimenti?.conto?.descrizione}
            </DialogTitle>
            <DialogDescription>
              {selectedContoMovimenti?.totale_movimenti || 0} movimenti trovati • 
              Tipo: {selectedContoMovimenti?.conto?.tipo} • 
              Categoria: {selectedContoMovimenti?.conto?.categoria}
            </DialogDescription>
          </DialogHeader>
          
          <div className="overflow-auto max-h-[60vh]">
            {movimentiContoLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : selectedContoMovimenti?.movimenti?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Descrizione</TableHead>
                    <TableHead>Documento</TableHead>
                    <TableHead className="text-center">D/A</TableHead>
                    <TableHead className="text-right">Importo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedContoMovimenti.movimenti.map((mov, idx) => (
                    <TableRow key={mov.id || idx}>
                      <TableCell className="font-mono text-sm">
                        {mov.data_registrazione}
                      </TableCell>
                      <TableCell className="max-w-[300px] truncate">
                        {mov.descrizione}
                      </TableCell>
                      <TableCell className="font-mono text-sm text-zinc-400">
                        {mov.documento_riferimento || "-"}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant={mov.tipo_movimento_conto === "dare" ? "default" : "secondary"}>
                          {mov.tipo_movimento_conto === "dare" ? "D" : "A"}
                        </Badge>
                      </TableCell>
                      <TableCell className={`text-right font-semibold ${mov.tipo_movimento_conto === "dare" ? "text-green-400" : "text-red-400"}`}>
                        {mov.segno}{formatCurrency(mov.importo)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-zinc-500">
                <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Nessun movimento registrato per questo conto</p>
              </div>
            )}
          </div>
          
          {selectedContoMovimenti?.movimenti?.length > 0 && (
            <div className="border-t border-zinc-800 pt-4 mt-4">
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Totale DARE:</span>
                <span className="text-green-400 font-semibold">
                  {formatCurrency(selectedContoMovimenti.movimenti.filter(m => m.tipo_movimento_conto === "dare").reduce((s, m) => s + m.importo, 0))}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Totale AVERE:</span>
                <span className="text-red-400 font-semibold">
                  {formatCurrency(selectedContoMovimenti.movimenti.filter(m => m.tipo_movimento_conto === "avere").reduce((s, m) => s + m.importo, 0))}
                </span>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setShowMovimentiContoDialog(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
